import { FORM_ERROR } from "final-form";
import { Field, Form } from "react-final-form";

import { changePassword, ChangePasswordPayload } from "@/api/endpoints/user";
import {
  sequenceValidators,
  validateLength,
  validateMatches,
  validateRequired,
} from "@/utils/validators";
import Button from "@components/Button";
import { FF_PasswordField } from "@components/Field";
import Typography from "@components/Typography";
import { i18n } from "@lingui/core";
import { useLingui } from "@lingui/react/macro";

interface ChangePasswordProps {
  onSuccess?: () => void;
  className?: string;
}

/**
 * ChangePassword
 * A ready-made container with all forms and API setups necessary to change a the current user password.
 * The autocomplete attributes are set to help password managers autofill the fields properly.
 */
const ChangePassword: React.FC<ChangePasswordProps> = ({
  onSuccess,
  className,
}) => {
  const { t } = useLingui();
  const handleSave = async (values: ChangePasswordPayload) =>
    changePassword(values)
      .then((response) => {
        if (!response.ok) {
          return { [FORM_ERROR]: response.data?.error };
        }
        onSuccess?.();
      })
      .catch((error) => {
        return {
          [FORM_ERROR]: error?.message || t`Någonting gick fel`,
        };
      });

  return (
    <>
      <Form
        onSubmit={(values) => handleSave(values as ChangePasswordPayload)}
        subscription={{
          values: true,
          valid: true,
          dirtySinceLastSubmit: true,
          submitError: true,
          pristine: true,
        }}
        validate={(values) => {
          if (values.new_password !== values.new_password_confirm) {
            return {
              new_password_confirm: t`Lösenorden matchar inte`,
            };
          }
          return undefined;
        }}
      >
        {({
          handleSubmit,
          valid,
          submitError,
          dirtySinceLastSubmit,
          pristine,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className={className}>
              <Field
                autoComplete="current-password"
                block
                component={FF_PasswordField}
                label={t`Nuvarande lösenord`}
                name="password"
                validate={validateRequired(
                  t`Du måste ange ditt nuvarande lösenord`
                )}
              />
              <Field
                autoComplete="new-password"
                block
                component={FF_PasswordField}
                label={t`Nytt lösenord`}
                name="new_password"
                validate={sequenceValidators(
                  validateRequired(i18n._(L.USER_SETTINGS_VALIDATION_REQUIRED)),
                  validateLength(
                    i18n._(L.USER_SETTINGS_VALIDATION_PASSWORD_TOO_SHORT),
                    9
                  ),
                  validateMatches(
                    i18n._(L.USER_SETTINGS_VALIDATION_PASSWORD_MISSING_NUMBER),
                    new RegExp(/[0-9]/g)
                  )
                )}
              />
              <Field
                autoComplete="new-password"
                block
                component={FF_PasswordField}
                label={L.USER_SETTINGS_PASSWORD_AGAIN}
                name="new_password_confirm"
              />

              {submitError && !dirtySinceLastSubmit && (
                <Typography>{submitError}</Typography>
              )}
              <Button
                disabled={!valid || pristine}
                type="submit"
                data-testid="change-password-button"
              >
                {L.USER_SETTINGS_PASSWORD_SAVE}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </>
  );
};

export default ChangePassword;
